<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  BrandMark,
  ZoneRenderer,
  effectiveDurationMs,
  nextItemIndex,
  parseAspect,
  parseClockZoneParams,
  parseProgrammeZoneParams,
  programmeStringsFor,
  type ProgrammePayload,
  type ResolvedBinding,
} from '@signage42/player-renderer';
import { TimelineCursor, nowAdjusted } from '@/playback/timeline';
import { ProgrammeCache, programmeDaysNeeded } from '@/playback/programme';
import { SignalCache, type ActiveSignal } from '@/playback/signals';
import { isOverrideSegment, overrideSegment, overrideSegmentId } from '@/playback/override';
import { pinEmergencyContent, pinnedPlaylistId } from '@/playback/emergency';
import {
  invalidateLayout,
  invalidatePlaylist,
  loadLayout,
  loadPlaylist,
} from '@/playback/playlist';
import {
  TRANSITION_DURATION_MS,
  effectiveTransition,
  isTransientZone,
  type TransitionKind,
} from '@/playback/transitions';
import { startHeartbeat, getLastNetworkOkAt } from '@/telemetry/heartbeat';
import { connectPlayerSse } from '@/telemetry/sse';
import { createCommandRunner } from '@/telemetry/commands';
import { claimAgentEnrollment } from '@/telemetry/agent-claim';
import { reloadBehindCover, shouldReloadNow } from '@/update/sw-update';
import { clearTokens, readTokens } from '@/api/tokenStore';
import { pushWarning } from '@/lib/warningsBuffer';
import DiagnosticOverlay from '@/components/DiagnosticOverlay.vue';
import { BUILD_VERSION } from '@/main';
import type { Connectivity } from '@/telemetry/heartbeat';
import type {
  DiagnosticsSnapshot,
  Layout,
  LayoutZone,
  Playlist,
  PlaylistItem,
  TimelineItem,
  ZoneBindingMedia,
  ZoneBindingText,
  ZoneBindingUrl,
} from '@/lib/types';

const router = useRouter();

const cursor = new TimelineCursor();
const programme = new ProgrammeCache();
/**
 * Live venue state: the interval has started, the house is being evacuated. Fed
 * by the event channel and by its own endpoint, and every entry carries an
 * expiry it releases itself at, so a screen that stops hearing from the server
 * mid-interval returns to its programme rather than holding a "Pause" notice all
 * evening (D9).
 */
const signals = new SignalCache();
const programmePayload = shallowRef<ProgrammePayload | null>(null);
const currentSegment = shallowRef<TimelineItem | null>(null);
const currentPlaylist = shallowRef<Playlist | null>(null);
const currentLayout = shallowRef<Layout | null>(null);
const currentItemIndex = ref(-1);
const currentItem = computed<PlaylistItem | null>(() => {
  const pl = currentPlaylist.value;
  if (!pl || currentItemIndex.value < 0) return null;
  return pl.items[currentItemIndex.value] ?? null;
});

// ---------------------------------------------------------------------------
// Transition state — set by setupNextTransition() one transition-duration
// before the slot boundary; cleared at the boundary when advanceItem swaps.
// ---------------------------------------------------------------------------
const incomingItemIndex = ref<number>(-1);
const incomingLayout = shallowRef<Layout | null>(null);
const transitionPhase = ref<'idle' | 'active'>('idle');
const transitionType = ref<TransitionKind>('cut');
let transitionTimer: ReturnType<typeof setTimeout> | null = null;

const incomingItem = computed<PlaylistItem | null>(() => {
  const pl = currentPlaylist.value;
  if (!pl || incomingItemIndex.value < 0) return null;
  return pl.items[incomingItemIndex.value] ?? null;
});

function clearTransitionState(): void {
  if (transitionTimer) {
    clearTimeout(transitionTimer);
    transitionTimer = null;
  }
  incomingItemIndex.value = -1;
  incomingLayout.value = null;
  transitionPhase.value = 'idle';
}

/**
 * A layout may ask for a wider programme window than the cached payload covers —
 * a "next month" board scheduled alongside a "tonight" one. Widening here rather
 * than at fetch time keeps the request driven by what is actually on the wall.
 */
watch(currentLayout, (layout) => {
  if (!layout) return;
  const needed = programmeDaysNeeded(layout.zones);
  if (needed !== null) programme.ensureDays(needed);
});

/**
 * The winning override, mirrored into a ref so the message it carries can
 * render. The playlist says what an emergency looks like; the message is the
 * part only tonight's operator knows ("Sammelplatz Nordhof"), typed at the
 * raise and rendered as a banner over the override content.
 */
const overrideSignal = shallowRef<ActiveSignal | null>(null);
const signalMessage = computed<{ text: string; kind: ActiveSignal['kind'] } | null>(() => {
  const sig = overrideSignal.value;
  if (sig === null || sig.message === null || sig.message.trim() === '') return null;
  // Only over the takeover itself — never over the scheduled programme during
  // the moment between the notification and the segment swap.
  const seg = currentSegment.value;
  if (seg === null || seg.scheduleId !== overrideSegmentId(sig)) return null;
  return { text: sig.message, kind: sig.kind };
});

const connectivity = ref<Connectivity>('online');
const lastHeartbeatAckAt = ref<Date | null>(null);
const showDiagnostics = ref(false);
let diagnosticsHideTimer: ReturnType<typeof setTimeout> | null = null;

let segmentTimer: ReturnType<typeof setTimeout> | null = null;
let itemTimer: ReturnType<typeof setTimeout> | null = null;
let stopHeartbeat: (() => void) | null = null;
let stopSse: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Letterbox math
// ---------------------------------------------------------------------------
const stageStyle = computed(() => {
  const layout = currentLayout.value;
  if (!layout) return { width: '100%', height: '100%' };
  const ratio = parseAspect(layout.aspectRatio, layout.customAspect);
  if (!ratio) return { width: '100%', height: '100%' };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const vRatio = vw / vh;
  if (vRatio > ratio) {
    // viewport wider than layout — pillarbox: shrink width to vh*ratio
    const w = vh * ratio;
    return {
      width: `${w}px`,
      height: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
      position: 'relative' as const,
    };
  }
  // viewport taller — letterbox: shrink height to vw/ratio
  const h = vw / ratio;
  return {
    width: '100%',
    height: `${h}px`,
    marginTop: `${(vh - h) / 2}px`,
    position: 'relative' as const,
  };
});

// ---------------------------------------------------------------------------
// Segment playback
// ---------------------------------------------------------------------------
async function loadSegment(seg: TimelineItem | null): Promise<void> {
  if (segmentTimer) {
    clearTimeout(segmentTimer);
    segmentTimer = null;
  }
  if (itemTimer) {
    clearTimeout(itemTimer);
    itemTimer = null;
  }
  clearTransitionState();
  currentSegment.value = seg;
  currentItemIndex.value = -1;
  currentPlaylist.value = null;
  currentLayout.value = null;
  if (!seg) {
    armNextStart();
    return;
  }
  try {
    const playlist = await loadPlaylist(seg.playlistId);
    currentPlaylist.value = playlist;
    if (seg.layoutId) {
      currentLayout.value = await loadLayout(seg.layoutId);
    }
    advanceItem();
  } catch (err) {
    pushWarning(`segment-load-failed:${seg.scheduleId}:${(err as Error).message ?? 'err'}`);
  }
  armSegmentEnd(seg);
}

/**
 * Idle until the next scheduled item starts. Without this an idle screen — after
 * boot, or in a gap between schedules — waited for the next timeline refetch,
 * up to 15 min late (seen on a Pi: a one-shot starting 36 s after load stayed
 * on the idle screen).
 */
function armNextStart(): void {
  const next = cursor.upcoming();
  if (!next) return;
  segmentTimer = setTimeout(() => {
    pickAndLoadActiveSegment();
  }, next.inMs + 50);
}

/**
 * Re-evaluates what should be playing when this segment's window closes.
 *
 * For a timeline segment that is the next schedule boundary; for a signal
 * override it is the expiry, which makes the ordinary segment timer the failsafe
 * release as well (D9) — a screen that stops hearing from the server mid-interval
 * still returns to its programme.
 */
function armSegmentEnd(seg: TimelineItem): void {
  if (segmentTimer) {
    clearTimeout(segmentTimer);
    segmentTimer = null;
  }
  const remaining = Date.parse(seg.end) - nowAdjusted();
  if (remaining > 0) {
    segmentTimer = setTimeout(() => {
      pickAndLoadActiveSegment();
    }, remaining + 50);
  } else {
    pickAndLoadActiveSegment();
  }
}

/**
 * For sequential playlists: derive the (clip index, ms until next boundary)
 * from wall clock alone, so two players running the same playlist stay in
 * lockstep without any peer-to-peer protocol.
 */
function sequentialPosition(
  items: ReadonlyArray<Pick<PlaylistItem, 'durationMs' | 'zones'>>,
  anchorMs: number,
  nowMs: number,
): { index: number; msUntilNext: number } | null {
  if (items.length === 0) return null;
  const durations = items.map((it) => Math.max(1, effectiveDurationMs(it, it.zones)));
  const cycleMs = durations.reduce((a, b) => a + b, 0);
  if (cycleMs <= 0) return null;
  let offset = (((nowMs - anchorMs) % cycleMs) + cycleMs) % cycleMs;
  for (let i = 0; i < durations.length; i++) {
    if (offset < durations[i]!) {
      return { index: i, msUntilNext: durations[i]! - offset };
    }
    offset -= durations[i]!;
  }
  return { index: 0, msUntilNext: durations[0]! };
}

/**
 * A new player build has taken control of this page (D14): reload into it if
 * now is a good moment. At an item boundary nothing visible is interrupted;
 * from the cap timer it is the fallback for a screen with no boundaries at all.
 */
function reloadForUpdateIfDue(atBoundary: boolean): boolean {
  const due = shouldReloadNow({
    atBoundary,
    overrideOnAir: isOverrideSegment(currentSegment.value),
    lastNetworkOkAt: getLastNetworkOkAt()?.getTime() ?? null,
    now: Date.now(),
  });
  if (due) void reloadBehindCover();
  return due;
}

async function advanceItem(): Promise<void> {
  // Before choosing the next item, and without arming its timer: the reload
  // replaces this page, and the new one starts the item itself.
  if (reloadForUpdateIfDue(true)) return;
  const playlist = currentPlaylist.value;
  const segment = currentSegment.value;
  if (!playlist || !segment) return;
  if (playlist.items.length === 0) {
    currentItemIndex.value = -1;
    return;
  }

  let next: number;
  let scheduleInMs: number;
  if (playlist.playbackMode === 'sequential') {
    const pos = sequentialPosition(playlist.items, Date.parse(segment.anchor), nowAdjusted());
    if (!pos) return;
    next = pos.index;
    // +5 ms guard so the next setTimeout fires *after* the boundary, not on it
    // (avoids picking the same clip twice due to floating-point edges).
    scheduleInMs = pos.msUntilNext + 5;
  } else {
    next = nextItemIndex(playlist, currentItemIndex.value);
    if (next < 0) return;
    const item = playlist.items[next];
    if (!item) return;
    scheduleInMs = effectiveDurationMs(item, item.zones);
  }

  const nextItem = playlist.items[next];
  if (!nextItem) return;

  // If we already pre-loaded `next` as the "incoming" during a transition,
  // promote it cheaply instead of re-fetching the layout.
  const preloaded =
    incomingItemIndex.value === next &&
    incomingLayout.value &&
    incomingLayout.value.id === nextItem.layoutId;

  if (preloaded) {
    currentLayout.value = incomingLayout.value;
  } else if (!currentLayout.value || currentLayout.value.id !== nextItem.layoutId) {
    try {
      currentLayout.value = await loadLayout(nextItem.layoutId);
    } catch (err) {
      pushWarning(`layout-load-failed:${nextItem.layoutId}:${(err as Error).message ?? 'err'}`);
      // Skip the item if its layout is unreachable.
      currentItemIndex.value = next;
      clearTransitionState();
      itemTimer = setTimeout(() => void advanceItem(), 1000);
      return;
    }
  }
  currentItemIndex.value = next;
  clearTransitionState();
  itemTimer = setTimeout(() => void advanceItem(), scheduleInMs);

  // Pre-schedule the visual transition for the upcoming swap so it can start
  // half-a-second (or whatever the type's duration is) before slot end.
  setupNextTransition(next, scheduleInMs);
}

/**
 * Peek the index of the item that would come after `currentIdx`. For
 * sequential playlists this is deterministic; for weighted_random it's the
 * existing rng-driven helper (rng ordering shifts by one tick, which is
 * irrelevant to cross-screen sync because random mode can't sync anyway).
 */
function peekNextIndex(playlist: Playlist, currentIdx: number): number {
  if (playlist.playbackMode === 'sequential') {
    if (playlist.items.length === 0) return -1;
    return (currentIdx + 1) % playlist.items.length;
  }
  return nextItemIndex(playlist, currentIdx);
}

async function setupNextTransition(currentIdx: number, msUntilSwap: number): Promise<void> {
  const playlist = currentPlaylist.value;
  if (!playlist) return;
  const futureIdx = peekNextIndex(playlist, currentIdx);
  if (futureIdx < 0) return;
  const fromItem = playlist.items[currentIdx];
  const toItem = playlist.items[futureIdx];
  if (!fromItem || !toItem) return;

  const trans = effectiveTransition(fromItem, toItem);
  if (trans === 'cut') return;
  const dur = TRANSITION_DURATION_MS[trans];
  // Skip the transition if the slot is too short to host it cleanly.
  if (msUntilSwap <= dur + 50) return;

  if (transitionTimer) clearTimeout(transitionTimer);
  transitionTimer = setTimeout(async () => {
    // Pre-load the incoming layout if it differs from the current one.
    let layoutForIncoming = currentLayout.value;
    if (!layoutForIncoming || layoutForIncoming.id !== toItem.layoutId) {
      try {
        layoutForIncoming = await loadLayout(toItem.layoutId);
      } catch {
        // Skip the transition silently; advanceItem will load on swap.
        return;
      }
    }
    // Race guard: if advanceItem already swapped while we were loading,
    // bail out.
    if (currentItemIndex.value !== currentIdx) return;
    incomingLayout.value = layoutForIncoming;
    incomingItemIndex.value = futureIdx;
    transitionType.value = trans;
    transitionPhase.value = 'active';
  }, msUntilSwap - dur);
}

/**
 * The one place that decides what is on the screen.
 *
 * An active venue signal sits above the resolved timeline, in the fixed order
 * emergency > delay > intermission > schedules, ranked in `SignalCache` (D9). The
 * ordering is not operator-configurable and is not a field on anything: a
 * priority *field* is an invitation to configure an emergency into second place.
 *
 * Release needs no special handling. When the override lifts — cleared, or
 * expired with nobody watching — this runs again and the timeline is consulted
 * for the *current* wall-clock time, so playback resumes at the position the
 * schedule dictates now rather than where it was interrupted.
 */
function pickAndLoadActiveSegment(): void {
  const signal = signals.active();
  const override = signal === null ? null : overrideSegment(signal);
  if (override) {
    const playing = currentSegment.value;
    if (
      playing &&
      playing.scheduleId === override.scheduleId &&
      playing.playlistId === override.playlistId
    ) {
      // The same signal, extended: keep playing and move the release deadline.
      // Restarting the playlist here would visibly reset an interval notice
      // every time a cue system re-sent the signal it had already sent.
      currentSegment.value = override;
      armSegmentEnd(override);
      return;
    }
    void loadSegment(override);
    return;
  }
  const active = cursor.active();
  void loadSegment(active?.item ?? null);
}

function onZoneWarn(message: string): void {
  pushWarning(message);
}

const transientZonesCurrent = computed(() =>
  (currentLayout.value?.zones ?? []).filter((z) => isTransientZone(z)),
);
const continuousZones = computed(() =>
  (currentLayout.value?.zones ?? []).filter((z) => !isTransientZone(z)),
);
const transientZonesIncoming = computed(() => {
  const layout = incomingLayout.value ?? currentLayout.value;
  return (layout?.zones ?? []).filter((z) => isTransientZone(z));
});

const transitionDurationCss = computed(() => `${TRANSITION_DURATION_MS[transitionType.value]}ms`);
const isTransitioning = computed(() => transitionPhase.value === 'active');
const isCrossfade = computed(() => isTransitioning.value && transitionType.value === 'crossfade');
const isFade = computed(() => isTransitioning.value && transitionType.value === 'fade');

function resolveBinding(item: PlaylistItem | null, zone: LayoutZone): ResolvedBinding {
  // Programme zones take no playlist binding at all: their content is their own
  // parameters joined against the cached programme payload (D6). Checked before
  // the item, so the zone renders even on an item that binds nothing — which is
  // the normal case for a board whose playlist exists only to place the zones.
  const programmeParams = parseProgrammeZoneParams(zone.parameters);
  if (programmeParams) {
    const payload = programmePayload.value;
    if (!payload) return { kind: 'empty' };
    return {
      kind: 'programme',
      params: programmeParams,
      payload,
      // Chrome in the screen's locale, independent of the language the venue
      // publishes its titles in.
      strings: programmeStringsFor(payload.locale),
    };
  }
  // A clock is likewise self-contained: its parameters are its content.
  const clockParams = parseClockZoneParams(zone.parameters);
  if (clockParams) return { kind: 'clock', params: clockParams };
  if (!item) return { kind: 'empty' };
  const binding = item.zones[zone.name];
  if (!binding) return { kind: 'empty' };
  if (binding.kind === 'media') {
    const m = binding as ZoneBindingMedia;
    if (!m.downloadUrl) return { kind: 'empty' };
    return { kind: 'media', src: m.downloadUrl, mimeType: m.mimeType ?? '' };
  }
  if (binding.kind === 'url') {
    const u = binding as ZoneBindingUrl;
    return { kind: 'url', src: u.url, sandboxProfile: u.sandboxProfile };
  }
  if (binding.kind === 'text') {
    return { kind: 'text', text: (binding as ZoneBindingText).text };
  }
  return { kind: 'empty' };
}

// ---------------------------------------------------------------------------
// Connectivity tracking
// ---------------------------------------------------------------------------
function recomputeConnectivity(): void {
  if (!navigator.onLine) {
    connectivity.value = 'offline';
    return;
  }
  const last = getLastNetworkOkAt();
  if (!last) {
    connectivity.value = 'online';
    return;
  }
  const ageMs = Date.now() - last.getTime();
  connectivity.value = ageMs > 90_000 ? 'degraded' : 'online';
}

let connectivityTimer: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Diagnostic overlay
// ---------------------------------------------------------------------------
function toggleDiagnostics(): void {
  showDiagnostics.value = !showDiagnostics.value;
  if (showDiagnostics.value) {
    if (diagnosticsHideTimer) clearTimeout(diagnosticsHideTimer);
    diagnosticsHideTimer = setTimeout(() => {
      showDiagnostics.value = false;
    }, 60_000);
  } else if (diagnosticsHideTimer) {
    clearTimeout(diagnosticsHideTimer);
    diagnosticsHideTimer = null;
  }
}

function onKey(ev: KeyboardEvent): void {
  if (ev.shiftKey && ev.key === '?') toggleDiagnostics();
}

const cacheBytes = ref<number | null>(null);
async function refreshCacheEstimate(): Promise<void> {
  try {
    if ('storage' in navigator && navigator.storage.estimate) {
      const est = await navigator.storage.estimate();
      cacheBytes.value = est.usage ?? null;
    }
  } catch {
    cacheBytes.value = null;
  }
}

const diagnostics = computed<DiagnosticsSnapshot>(() => {
  const tokens = readTokens();
  const seg = currentSegment.value;
  const active = cursor.active();
  const override = signals.active();
  return {
    buildVersion: BUILD_VERSION,
    deviceId: tokens?.deviceId ?? null,
    screenId: null, // not surfaced via tokens; admin shows this on revocation event
    currentSegmentId: seg?.scheduleId ?? null,
    currentSegmentRemainingMs: active?.remainingMs ?? null,
    currentItemId: currentItem.value?.id ?? null,
    connectivity: connectivity.value,
    signalOverride:
      isOverrideSegment(seg) && override !== null ? `${override.kind}:${override.id}` : null,
    lastHeartbeatAckAt: lastHeartbeatAckAt.value?.toISOString() ?? null,
    cacheBytes: cacheBytes.value,
  };
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
/** Remote commands from the admin UI (D1, D7). */
const commands = createCommandRunner({
  buildVersion: BUILD_VERSION,
  reload: () => reloadBehindCover(),
});
let updateCapTimer: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
  // Before anything else that can fail: if this page is the result of a remote
  // reload, say so, so the admin sees `succeeded` within seconds.
  void commands.confirmAfterBoot();
  // Vouch for the Pi's root agent, if the launcher handed us its code (D3).
  void claimAgentEnrollment();

  // The programme fetch is not awaited: a venue feed that is slow or down must
  // not delay the start of scheduled playback. Programme zones render empty for
  // the moment it takes, then fill in.
  programme.subscribe((payload) => {
    programmePayload.value = payload;
  });
  void programme.start();

  // Awaited, unlike the programme: a player restarting during an interval must
  // not put twenty seconds of tonight's teaser on a foyer wall before it learns
  // that the audience is at the bar (specs/player). The read is bounded — the
  // fetch swallows its own failures — and the timeline read below is awaited for
  // the same reason anyway.
  await signals.start();

  await cursor.start();
  cursor.subscribe(() => {
    pickAndLoadActiveSegment();
  });
  // Every transition of the winning override — raised, outranked, cleared,
  // expired — re-decides what is on the screen.
  signals.subscribe((signal: ActiveSignal | null) => {
    if (signal !== null) {
      pushWarning(`signal-override:${signal.kind}:${signal.id}`);
    }
    overrideSignal.value = signal;
    pickAndLoadActiveSegment();
  });
  // Pinned whether or not an emergency is happening, and re-pinned whenever the
  // house changes what an evacuation shows (D10). Not awaited: pre-caching a
  // video must not delay the start of playback.
  signals.subscribeEmergencyPlaylist((playlistId) => {
    void pinEmergencyContent(playlistId);
  });

  stopHeartbeat = startHeartbeat({
    playerVersion: BUILD_VERSION,
    getCurrentItemId: () => currentItem.value?.id ?? null,
    getConnectivity: () => connectivity.value,
    onAck: (at) => {
      lastHeartbeatAckAt.value = at;
    },
  });

  stopSse = connectPlayerSse({
    onScheduleRematerialized: () => {
      void cursor.refetch().catch(() => undefined);
    },
    onPlaylistPublished: (playlistId) => {
      const seg = currentSegment.value;
      invalidatePlaylist(playlistId);
      if (seg && seg.playlistId === playlistId) {
        void loadPlaylist(playlistId, true).then((p) => {
          currentPlaylist.value = p;
        });
      }
      // A new revision of the emergency playlist means the pinned copy is now
      // the wrong content, and it is pinned precisely because it will not be
      // fetchable when it is needed.
      if (pinnedPlaylistId() === playlistId) {
        void pinEmergencyContent(playlistId);
      }
    },
    onLayoutPublished: (layoutId) => {
      invalidateLayout(layoutId);
      if (currentLayout.value && currentLayout.value.id === layoutId) {
        void loadLayout(layoutId, true).then((l) => {
          currentLayout.value = l;
        });
      }
    },
    onDeviceRevoked: () => {
      clearTokens();
      void router.replace({ name: 'pair' });
    },
    onSignalChanged: (payload) => {
      signals.applyEvent(payload);
    },
    onDeviceCommand: () => {
      void commands.drain();
    },
    onOpen: () => {
      void commands.drain();
      // A schedule published while the stream was down (or before the network
      // came up after a boot, when the service worker answered the first fetch
      // from its cache) is otherwise only seen at the next 15-min refetch.
      void cursor.refetch().catch(() => undefined);
    },
  });

  window.addEventListener('keydown', onKey);
  window.addEventListener('online', recomputeConnectivity);
  window.addEventListener('offline', recomputeConnectivity);
  connectivityTimer = setInterval(recomputeConnectivity, 30_000);
  updateCapTimer = setInterval(() => reloadForUpdateIfDue(false), 60_000);
  recomputeConnectivity();

  void refreshCacheEstimate();
});

onBeforeUnmount(() => {
  cursor.stop();
  programme.stop();
  signals.stop();
  if (segmentTimer) clearTimeout(segmentTimer);
  if (itemTimer) clearTimeout(itemTimer);
  if (transitionTimer) clearTimeout(transitionTimer);
  if (diagnosticsHideTimer) clearTimeout(diagnosticsHideTimer);
  if (connectivityTimer) clearInterval(connectivityTimer);
  if (updateCapTimer) clearInterval(updateCapTimer);
  stopHeartbeat?.();
  stopSse?.();
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('online', recomputeConnectivity);
  window.removeEventListener('offline', recomputeConnectivity);
});
</script>

<template>
  <main class="stage-frame">
    <div class="stage" :style="{ ...stageStyle, '--transition-dur': transitionDurationCss }">
      <template v-if="currentLayout">
        <!-- Continuous zones (ticker, clock, live feed, schedule list) stay
             mounted across item changes and do NOT participate in transitions. -->
        <ZoneRenderer
          v-for="zone in continuousZones"
          :key="`cont-${zone.id}`"
          :zone="zone"
          :binding="resolveBinding(currentItem, zone)"
          @warn="onZoneWarn"
        />

        <!-- Outgoing layer: transient zones bound to the current item.
             Opacity animates during a transition. -->
        <div
          class="layer outgoing"
          :class="{
            'is-crossfade': isCrossfade,
            'is-fade': isFade,
            transitioning: isTransitioning,
          }"
        >
          <ZoneRenderer
            v-for="zone in transientZonesCurrent"
            :key="`out-${zone.id}`"
            :zone="zone"
            :binding="resolveBinding(currentItem, zone)"
            @warn="onZoneWarn"
          />
        </div>

        <!-- Incoming layer: transient zones bound to the incoming item.
             Only mounted during the transition window. -->
        <div
          v-if="incomingItem && isTransitioning"
          class="layer incoming"
          :class="{ 'is-crossfade': isCrossfade, 'is-fade': isFade }"
        >
          <ZoneRenderer
            v-for="zone in transientZonesIncoming"
            :key="`in-${zone.id}`"
            :zone="zone"
            :binding="resolveBinding(incomingItem, zone)"
            @warn="onZoneWarn"
          />
        </div>

        <!-- Black overlay for fade-through-black. Renders over the transient
             layers; opacity 0 → 1 → 0 across the transition window. -->
        <div v-if="isFade" class="black-overlay" aria-hidden="true" />
      </template>
      <div v-else class="idle">
        <BrandMark class="idle-mark" />
      </div>
    </div>
    <div v-if="signalMessage" class="signal-message" :data-kind="signalMessage.kind" role="alert">
      {{ signalMessage.text }}
    </div>
    <DiagnosticOverlay v-if="showDiagnostics" :snapshot="diagnostics" />
  </main>
</template>

<style scoped>
.stage-frame {
  width: 100vw;
  height: 100vh;
  background: #000;
  overflow: hidden;
  position: relative;
}
.stage {
  position: relative;
  background: #000;
  /* Fence zone z-index (the API accepts up to 1_000_000) inside the stage:
     without this a zone outstacks the signal banner (z-index 10) and the
     diagnostics overlay, and an evacuation message never reaches the glass. */
  isolation: isolate;
}
.idle {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
}
.idle-mark {
  /* Held well below full brightness on purpose: this sits on a wall for hours
     whenever a connected screen has nothing scheduled, and a bright static
     wordmark is what burns a panel. */
  font-size: 96px;
  color: rgba(246, 238, 220, 0.4);
}

/* Signal message banner ---------------------------------------------------- */
/* Sits on the frame, not the stage: a letterboxed layout must not letterbox an
   evacuation notice. Kind colors match the seeded emergency pack. */
.signal-message {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 2.5vh 5vw;
  text-align: center;
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    sans-serif;
  font-size: 4.2vh;
  font-weight: 650;
  line-height: 1.35;
  color: #fff;
  background: rgba(20, 20, 20, 0.92);
  /* The seeded emergency slides use the same reds; without a separator the
     banner's edge vanishes against them. */
  border-top: 0.5vh solid rgba(255, 255, 255, 0.9);
  overflow-wrap: break-word;
}
.signal-message[data-kind='emergency'] {
  background: #b3261e;
}
.signal-message[data-kind='delay'] {
  background: #9a5b00;
}

/* Transition layers ------------------------------------------------------- */
.layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
/* Opacity animates on the zones themselves, never on the .layer wrapper: an
   animated opacity would make the wrapper a stacking context at level 0 and
   collapse zone z-index ordering against continuous zones for the duration of
   every transition. The zones carry an inline z-index, so they are stacking
   contexts already and the animation changes nothing else. */
/* Crossfade: outgoing fades out, incoming fades in, both over the full
   transition window. */
.layer.outgoing.is-crossfade.transitioning :deep(.lr42-zone) {
  animation: layer-fade-out var(--transition-dur) ease forwards;
}
.layer.incoming.is-crossfade :deep(.lr42-zone) {
  animation: layer-fade-in var(--transition-dur) ease forwards;
}
/* Fade-through-black: outgoing fades out during the first half, incoming
   fades in during the second half. The black overlay handles the visual. */
.layer.outgoing.is-fade.transitioning :deep(.lr42-zone) {
  animation: layer-fade-out-half var(--transition-dur) ease forwards;
}
.layer.incoming.is-fade :deep(.lr42-zone) {
  animation: layer-fade-in-half var(--transition-dur) ease forwards;
}
.black-overlay {
  position: absolute;
  inset: 0;
  background: #000;
  pointer-events: none;
  /* Above the maximum zone zIndex the API accepts (1_000_000): the fade must
     black out every zone regardless of how the layout stacks them. Fenced by
     .stage's isolation, so it stays below the signal banner. */
  z-index: 1000001;
  animation: black-pulse var(--transition-dur) ease forwards;
}

@keyframes layer-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
@keyframes layer-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes layer-fade-out-half {
  from {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  to {
    opacity: 0;
  }
}
@keyframes layer-fade-in-half {
  from {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes black-pulse {
  from {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
</style>
